import { ArrowLeft, Columns2, Moon, Sun } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useParams, type RouteObject } from 'react-router-dom';
import { MainMenu } from '@/components/layout/shell/MainMenu';
import { WorkspaceCanvas } from '@/components/layout/workspace/WorkspaceCanvas';
import { WorkspaceProvider } from '@/components/layout/workspace/WorkspaceProvider';
import { useWorkspace } from '@/components/layout/workspace/workspaceContext';
import type { RightPanelConfig, RightPanelItem } from '@/components/layout/workspace/rightPanelContext';
import { ObserverProvider } from '@/components/observer/ObserverProvider';
import {
  Avatar,
  Badge,
  Button,
  EmptyState,
  FilePreview,
  IconButton,
  PropertyList,
  PersonProperties,
  PropertyRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  ToastProvider,
} from '@/components/ui';
import { useTheme } from '@/theme/useTheme';
import { PersonTimeZoneContext } from '@/lib/personTimeZone';
import { ConversationDemo, MarkupDemo } from './ConversationDemo';
import { InboxDemo, InboxSource, ResponseTimeDemo } from './InboxDemo';
import { InboxSampleContext } from './inboxSampleContext';
import { makeInboxEntries } from './inboxModel';
import { PeopleDemo, TemplatesDemo } from './PeopleTemplatesDemo';
import { PEOPLE } from './personModel';
import { useSampleMessageActions } from './useSampleMessageActions';
import { DEMO_AUTHOR, ENTITIES, FILES, HOME, resolveConversation } from './model';

const routes: RouteObject[] = [
  { path: '/source/:id', element: <SourcePage /> },
  { path: '/file/:id', element: <DetailPage kind="file" /> },
  { path: '/entity/:id', element: <DetailPage kind="entity" /> },
  { path: '*', element: <Examples /> },
];
export function ConversationsPatterns() {
  const [entries, setEntries] = useState(() => makeInboxEntries(Date.now()));
  const inboxData = useMemo(() => ({ entries, setEntries }), [entries]);
  const [embedded] = useState(() => new URLSearchParams(window.location.search).has('embedded'));
  return (
    <InboxSampleContext.Provider value={inboxData}>
      <ToastProvider>
        <WorkspaceProvider
          homePath={HOME}
          resolve={resolveConversation}
          storageKey={'kaname.conversations.workspace.' + (embedded ? 'embedded' : 'page') + '.v1'}
        >
          <ObserverProvider home="core" storageKey="kaname.conversations.observer.v1">
            <SampleShell embedded={embedded} />
          </ObserverProvider>
        </WorkspaceProvider>
      </ToastProvider>
    </InboxSampleContext.Provider>
  );
}
function SampleShell({ embedded }: { embedded: boolean }) {
  const ws = useWorkspace();
  const { mode, toggle } = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [timeMode, setTimeMode] = useState<'moscow' | 'local'>('moscow');
  const display = {
    timeZone: timeMode === 'moscow' ? 'Europe/Moscow' : Intl.DateTimeFormat().resolvedOptions().timeZone,
    label: timeMode === 'moscow' ? 'МСК' : 'Местное',
    mode: timeMode,
    onModeChange: setTimeMode,
  };
  const panel = useMemo<RightPanelConfig>(
    () => ({
      storageKey: 'kaname.conversations.panels.v1',
      render: (item) => <Details item={item} />,
    }),
    [],
  );
  return (
    <PersonTimeZoneContext.Provider value={display}>
      <div className="flex h-screen flex-col bg-frame text-fg">
        {!embedded && (
          <header className="flex shrink-0 flex-wrap items-center gap-4 border-b border-line bg-canvas px-6 py-3">
            <Link
              to="/design#conversation-patterns"
              className="inline-flex items-center gap-2 text-xs font-medium text-fg-muted hover:text-brand-fg"
            >
              <ArrowLeft className="size-4" />
              Галерея
            </Link>
            <div className="min-w-0 flex-1">
              <h1 className="type-subheading">Переписка и разметка</h1>
              <p className="text-xs text-fg-muted">Сообщения · ветки · общие элементы</p>
            </div>
            <Badge>Образец</Badge>
            <Button variant="secondary" size="sm" onClick={() => ws.openBeside(HOME + '/entity/anna')}>
              <Columns2 className="size-4" />
              Второе окно
            </Button>
            <IconButton
              variant="secondary"
              aria-label={mode === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
              onClick={toggle}
            >
              {mode === 'dark' ? <Sun /> : <Moon />}
            </IconButton>
          </header>
        )}
        <div data-shell="" className="relative flex min-h-0 flex-1 gap-3 p-3">
          <MainMenu
            expanded={expanded}
            onToggleExpand={() => setExpanded((value) => !value)}
            homeActive
            onHome={() => ws.go(HOME)}
            activeId={null}
            sections={[]}
            tools={[]}
            onSelect={() => ws.go(HOME)}
          />
          <WorkspaceCanvas routes={routes} rightPanel={panel} />
        </div>
      </div>
    </PersonTimeZoneContext.Provider>
  );
}
function Examples() {
  const actions = useSampleMessageActions();
  const [tab, setTab] = useState(() => {
    const demo =
      new URLSearchParams(window.location.search).get('demo') ??
      (window.location.pathname.includes('/source/') ? 'inbox' : null);
    return demo && ['markup', 'inbox', 'response', 'people', 'templates'].includes(demo) ? demo : 'feed';
  });
  return (
    <main className="mx-auto flex max-w-5xl min-w-0 flex-col gap-6 p-6">
      <header>
        <p className="mb-2 type-overline text-fg-subtle">Образцы · волна 3А</p>
        <h2
          data-panel-default-focus=""
          data-inbox-focus=""
          tabIndex={-1}
          className="type-title focus-visible:outline-none"
        >
          Переписка
        </h2>
        <p className="mt-2 max-w-3xl text-sm text-fg-muted">
          Один вид сообщений из разных каналов. Все участники, переписка и вложения вымышлены.
        </p>
      </header>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList aria-label="Элементы переписки">
          <TabsTrigger value="feed">Лента переписки</TabsTrigger>
          <TabsTrigger value="markup">Разметка сообщений</TabsTrigger>
          <TabsTrigger value="inbox">Единый ящик</TabsTrigger>
          <TabsTrigger value="response">Срок ответа</TabsTrigger>
          <TabsTrigger value="people">Человек и учётки</TabsTrigger>
          <TabsTrigger value="templates">Шаблоны ответов</TabsTrigger>
        </TabsList>
        <TabsContent value="feed" forceMount className="mt-6 data-[state=inactive]:hidden">
          <ConversationDemo />
        </TabsContent>
        <TabsContent value="inbox" forceMount className="mt-6 data-[state=inactive]:hidden">
          <InboxDemo />
        </TabsContent>
        <TabsContent value="response" forceMount className="mt-6 data-[state=inactive]:hidden">
          <ResponseTimeDemo />
        </TabsContent>
        <TabsContent value="people" forceMount className="mt-6 data-[state=inactive]:hidden">
          <PeopleDemo onEntityOpen={actions.onEntityOpen} />
        </TabsContent>
        <TabsContent value="templates" forceMount className="mt-6 data-[state=inactive]:hidden">
          <TemplatesDemo />
        </TabsContent>
        <TabsContent value="markup" forceMount className="mt-6 data-[state=inactive]:hidden">
          <MarkupDemo />
        </TabsContent>
      </Tabs>
    </main>
  );
}
function Details({ item }: { item: RightPanelItem }) {
  if (item.path.includes('/file/')) {
    const file = FILES.find((file) => file.id === item.id);
    return file ? <FilePreview file={file} /> : <EmptyState title="Вложение не найдено" />;
  }
  const entity =
    ENTITIES.find((entity) => entity.id === item.id) ??
    (item.id === DEMO_AUTHOR.id ? DEMO_AUTHOR : undefined);
  const person = PEOPLE.find((person) => person.id === item.id);
  if (!entity) return <EmptyState title="Запись не найдена" />;
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        {entity.kind === 'user' && <Avatar name={entity.name} size="lg" />}
        <div>
          <h3 className="type-heading">{entity.name}</h3>
          <p className="text-xs text-fg-muted">
            {entity.kind === 'user' ? 'Участник вымышленной переписки' : 'Канал вымышленной переписки'}
          </p>
        </div>
      </div>
      <PropertyList>
        <PropertyRow label="Имя">{entity.name}</PropertyRow>
        <PropertyRow label="Тип">{entity.kind === 'user' ? 'Человек' : 'Канал'}</PropertyRow>
      </PropertyList>
      {person && <PersonProperties person={person} />}
    </div>
  );
}
function SourcePage() {
  const { id = '' } = useParams();
  return <InboxSource entryId={id} />;
}
function DetailPage({ kind }: { kind: 'file' | 'entity' }) {
  const { id = '' } = useParams();
  return (
    <article className="p-6">
      <Details item={{ id, title: '', path: HOME + '/' + kind + '/' + id }} />
    </article>
  );
}
